import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateEmployee } from '@/hooks/useEmployees'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import type { Employee } from '@/types/entities'

interface EditEmployeeDialogProps {
  restaurantId: string
  employee: Employee
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function EditEmployeeDialog({
  restaurantId,
  employee,
  open,
  onOpenChange,
  onSuccess,
}: EditEmployeeDialogProps) {
  const { t } = useTranslation()
  const updateEmployeeMutation = useUpdateEmployee(restaurantId)

  const isSubmittingRef = useRef(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [formData, setFormData] = useState({
    nameAr: employee.nameAr,
    nameFr: employee.nameFr,
    positionAr: employee.positionAr || '',
    positionFr: employee.positionFr || '',
    monthlySalary: employee.monthlySalary,
    hireDate: employee.hireDate,
    isActive: employee.isActive,
  })

  useEffect(() => {
    setFormData({
      nameAr: employee.nameAr,
      nameFr: employee.nameFr,
      positionAr: employee.positionAr || '',
      positionFr: employee.positionFr || '',
      monthlySalary: employee.monthlySalary,
      hireDate: employee.hireDate,
      isActive: employee.isActive,
    })
    setErrors({})
  }, [employee])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmittingRef.current) return

    isSubmittingRef.current = true
    setIsSubmitting(true)
    setErrors({})

    const newErrors: Record<string, string> = {}
    if (!formData.nameAr && !formData.nameFr) {
      newErrors['nameAr'] = t('validation.required')
      newErrors['nameFr'] = t('validation.required')
    }
    if (!formData.monthlySalary || formData.monthlySalary <= 0) {
      newErrors['monthlySalary'] = t('validation.required')
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      isSubmittingRef.current = false
      setIsSubmitting(false)
      return
    }

    try {
      const payload: any = {
        employeeId: employee.id,
        nameAr: formData.nameAr,
        nameFr: formData.nameFr,
        monthlySalary: formData.monthlySalary,
        hireDate: formData.hireDate,
        isActive: formData.isActive,
      }
      if (formData.positionAr) payload.positionAr = formData.positionAr
      if (formData.positionFr) payload.positionFr = formData.positionFr

      await updateEmployeeMutation.mutateAsync(payload)
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      setErrors({ form: error instanceof Error ? error.message : t('error.generic') })
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] md:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('accounting.editEmployee')}</DialogTitle>
            <DialogDescription>{t('accounting.editEmployeeDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name AR */}
            <div className="space-y-2">
              <Label htmlFor="nameAr">{t('accounting.employeeName')} (AR) *</Label>
              <Input
                id="nameAr"
                value={formData.nameAr}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                className={errors['nameAr'] ? 'border-destructive' : ''}
                dir="rtl"
              />
              {errors['nameAr'] && (
                <p className="text-xs text-destructive">{errors['nameAr']}</p>
              )}
            </div>

            {/* Name FR */}
            <div className="space-y-2">
              <Label htmlFor="nameFr">{t('accounting.employeeName')} (FR) *</Label>
              <Input
                id="nameFr"
                value={formData.nameFr}
                onChange={(e) => setFormData({ ...formData, nameFr: e.target.value })}
                className={errors['nameFr'] ? 'border-destructive' : ''}
              />
              {errors['nameFr'] && (
                <p className="text-xs text-destructive">{errors['nameFr']}</p>
              )}
            </div>

            {/* Position AR */}
            <div className="space-y-2">
              <Label htmlFor="positionAr">{t('accounting.position')} (AR)</Label>
              <Input
                id="positionAr"
                value={formData.positionAr}
                onChange={(e) => setFormData({ ...formData, positionAr: e.target.value })}
                dir="rtl"
              />
            </div>

            {/* Position FR */}
            <div className="space-y-2">
              <Label htmlFor="positionFr">{t('accounting.position')} (FR)</Label>
              <Input
                id="positionFr"
                value={formData.positionFr}
                onChange={(e) => setFormData({ ...formData, positionFr: e.target.value })}
              />
            </div>

            {/* Monthly Salary */}
            <div className="space-y-2">
              <Label htmlFor="monthlySalary">{t('accounting.monthlySalary')} ({t('common.currency')}) *</Label>
              <Input
                id="monthlySalary"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.monthlySalary === 0 ? '' : formData.monthlySalary}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  setFormData({ ...formData, monthlySalary: isNaN(value) ? 0 : value });
                }}
                placeholder="0.00"
                className={errors['monthlySalary'] ? 'border-destructive' : ''}
              />
              {errors['monthlySalary'] && (
                <p className="text-xs text-destructive">{errors['monthlySalary']}</p>
              )}
            </div>

            {/* Hire Date */}
            <div className="space-y-2">
              <Label htmlFor="hireDate">{t('accounting.hireDate')}</Label>
              <Input
                id="hireDate"
                type="date"
                value={formData.hireDate}
                onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="isActive">{t('accounting.activeEmployee')}</Label>
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>

            {errors['form'] && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {errors['form']}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
